import React, { Fragment, FunctionComponent, useMemo, useState } from 'react';
import { Field } from 'react-final-form';
import { Typography } from '@mui/material';
import i18next from 'i18next';

import {
  composeValidators,
  digitsOnly,
  emailValidation,
  fileValidation,
  requiredValidation,
  onlyLetters,
} from 'helpers/validations';
import { getCountries } from 'helpers/countries';
import FieldRequired from 'components/commons/FieldRequired';

import MultiStepForm from 'components/commons/MultiStepForm';
import Input from 'components/inputs/Input';
import SelectInput from 'components/inputs/SelectInput';
import Checkbox from 'components/inputs/Checkbox';

import { ViewContainer, ViewContent, Title } from 'styles/view';
import { FormRowContainer } from 'styles/form';

import { IMarriagesFormData, IMarriagesFormProps } from './types';
import './i18n';

const MarriagesForm: FunctionComponent<IMarriagesFormProps> = (props: IMarriagesFormProps) => {
  const {
    cementeries,
    civilStates,
    files,
    idCardTypes,
    initialValues,
    isEditing,
    onSubmit,
    tribes,
  } = props;
  const [formValues, setFormValues] = useState<IMarriagesFormData>(initialValues);
  const countries = useMemo(() => getCountries(), []);

  const tabs = [
    i18next.t<string>('MARRIAGES_FORM:GROOM'),
    i18next.t<string>('MARRIAGES_FORM:BRIDE'),
    i18next.t<string>('MARRIAGES_FORM:PARENTS'),
    i18next.t<string>('MARRIAGES_FORM:GRANDPARENTS'),
    i18next.t<string>('MARRIAGES_FORM:WITNESSES'),
    i18next.t<string>('MARRIAGES_FORM:CEREMONY'),
    i18next.t<string>('MARRIAGES_FORM:COMMENTS'),
  ];

  const today = new Date().toISOString().split('T')[0];

  return (
    <ViewContainer auth data-testid="marriage-form">
      <Title>{i18next.t<string>('MARRIAGES_FORM:TITLE')}</Title>
      <ViewContent auth>
        <MultiStepForm<IMarriagesFormData>
          initialValues={initialValues}
          onSubmit={onSubmit}
          onValuesChange={setFormValues}
          tabs={tabs}
          tabsEnabled={isEditing}
        >
          {['groom', 'bride'].map((value) => (
            <MultiStepForm.Page key={`${value}-page`}>
              <Typography variant="h6">
                {i18next.t<string>(`MARRIAGES_FORM:${value.toUpperCase()}_INFORMATION`)}
              </Typography>
              <br />
              <FormRowContainer>
                <FieldRequired
                  label={i18next.t<string>('MARRIAGES_FORM:LAST_NAME')}
                  name={`${value}.lastName`}
                  render={Input}
                  validate={composeValidators(requiredValidation, onlyLetters)}
                />
                <FieldRequired
                  label={i18next.t<string>('MARRIAGES_FORM:FIRST_NAME')}
                  name={`${value}.firstName`}
                  render={Input}
                  validate={composeValidators(requiredValidation, onlyLetters)}
                />
              </FormRowContainer>
              <FormRowContainer>
                <Field
                  label={i18next.t<string>('MARRIAGES_FORM:ID_CARD_TYPE')}
                  name={`${value}.idCardType`}
                  options={idCardTypes}
                  render={SelectInput}
                  validate={requiredValidation}
                />
                <FieldRequired
                  label={i18next.t<string>('MARRIAGES_FORM:ID_CARD')}
                  name={`${value}.idCard`}
                  render={Input}
                  validate={requiredValidation}
                />
              </FormRowContainer>
              <FormRowContainer>
                <FieldRequired
                  label={i18next.t<string>('MARRIAGES_FORM:ID_CARD_FILE')}
                  name={`${value}.idCardFile`}
                  previewFiles={[
                    {
                      label: i18next.t<string>('MARRIAGES_FORM:ID_CARD_FILE'),
                      value: files?.[`${value}IdCardFile`],
                    },
                  ]}
                  render={Input}
                  type="file"
                  validate={
                    isEditing
                      ? fileValidation
                      : composeValidators(requiredValidation, fileValidation)
                  }
                />
                <FieldRequired
                  label={i18next.t<string>('MARRIAGES_FORM:ID_CARD_BACK_FILE')}
                  name={`${value}.idCardBackFile`}
                  previewFiles={[
                    {
                      label: i18next.t<string>('MARRIAGES_FORM:ID_CARD_BACK_FILE'),
                      value: files?.[`${value}IdCardBackFile`],
                    },
                  ]}
                  render={Input}
                  type="file"
                  validate={
                    isEditing
                      ? fileValidation
                      : composeValidators(requiredValidation, fileValidation)
                  }
                />
              </FormRowContainer>
              <FormRowContainer>
                <Field
                  label={i18next.t<string>('MARRIAGES_FORM:CIVIL_STATE')}
                  name={`${value}.civilState`}
                  options={civilStates}
                  render={SelectInput}
                  validate={requiredValidation}
                />
                <Field
                  label={i18next.t<string>('MARRIAGES_FORM:TRIBE')}
                  name={`${value}.tribe`}
                  options={tribes}
                  render={SelectInput}
                  validate={requiredValidation}
                />
              </FormRowContainer>
              <FormRowContainer>
                <FieldRequired
                  label={i18next.t<string>('MARRIAGES_FORM:ADDRESS')}
                  name={`${value}.address`}
                  render={Input}
                  validate={requiredValidation}
                />
                <FieldRequired
                  label={i18next.t<string>('MARRIAGES_FORM:APARTMENT')}
                  name={`${value}.apartment`}
                  render={Input}
                  validate={requiredValidation}
                />
              </FormRowContainer>
              <FormRowContainer>
                <FieldRequired
                  label={i18next.t<string>('MARRIAGES_FORM:LOCALITY')}
                  name={`${value}.locality`}
                  render={Input}
                  validate={requiredValidation}
                />
                <Field
                  label={i18next.t<string>('MARRIAGES_FORM:COUNTRY')}
                  name={`${value}.country`}
                  options={countries}
                  render={SelectInput}
                  validate={requiredValidation}
                />
              </FormRowContainer>
              <FormRowContainer>
                <FieldRequired
                  label={i18next.t<string>('MARRIAGES_FORM:BIRTHPLACE')}
                  name={`${value}.birthplace`}
                  render={Input}
                  validate={requiredValidation}
                />
                <FieldRequired
                  label={i18next.t<string>('MARRIAGES_FORM:BIRTHDATE')}
                  name={`${value}.birthdate`}
                  render={Input}
                  type="date"
                  max={today}
                  validate={requiredValidation}
                />
              </FormRowContainer>
              <FormRowContainer>
                <FieldRequired
                  label={i18next.t<string>('MARRIAGES_FORM:EMAIL')}
                  name={`${value}.email`}
                  render={Input}
                  type="email"
                  validate={composeValidators(requiredValidation, emailValidation)}
                />
                <FormRowContainer>
                  <FieldRequired
                    label={i18next.t<string>('MARRIAGES_FORM:AREA_CODE')}
                    maxLength={4}
                    name={`${value}.areaCode`}
                    parse={(v: string) => digitsOnly(v)}
                    render={Input}
                    validate={requiredValidation}
                  />
                  <FieldRequired
                    label={i18next.t<string>('MARRIAGES_FORM:PHONE')}
                    maxLength={8}
                    name={`${value}.phone`}
                    parse={(v: string) => digitsOnly(v)}
                    render={Input}
                    validate={requiredValidation}
                  />
                </FormRowContainer>
              </FormRowContainer>
              <FormRowContainer>
                <FieldRequired
                  label={i18next.t<string>('MARRIAGES_FORM:COMMUNITY')}
                  name={`${value}.community`}
                  render={Input}
                  validate={requiredValidation}
                />
                <FieldRequired
                  label={i18next.t<string>('MARRIAGES_FORM:JOB')}
                  name={`${value}.job`}
                  render={Input}
                  validate={requiredValidation}
                />
              </FormRowContainer>
              {formValues[value as 'groom' | 'bride'].civilState === 'DIVORCED' && (
                <FieldRequired
                  label={i18next.t<string>('MARRIAGES_FORM:GET_FILE')}
                  name={`${value}.getFile`}
                  previewFiles={[
                    {
                      label: i18next.t<string>('MARRIAGES_FORM:GET_FILE'),
                      value: files?.[`${value}GetFile`],
                    },
                  ]}
                  render={Input}
                  type="file"
                  validate={
                    isEditing
                      ? fileValidation
                      : composeValidators(requiredValidation, fileValidation)
                  }
                />
              )}
            </MultiStepForm.Page>
          ))}
          <MultiStepForm.Page key="parents-page">
            {['groom', 'bride'].map((value) => (
              <Fragment key={`${value}-parents`}>
                <Typography variant="h6">
                  {i18next.t<string>(`MARRIAGES_FORM:${value.toUpperCase()}_PARENTS_INFORMATION`)}
                </Typography>
                <br />
                <FormRowContainer>
                  <FieldRequired
                    label={i18next.t<string>('MARRIAGES_FORM:FATHER_LAST_NAME')}
                    name={`${value}.parents.father.lastName`}
                    render={Input}
                    validate={composeValidators(requiredValidation, onlyLetters)}
                  />
                  <FieldRequired
                    label={i18next.t<string>('MARRIAGES_FORM:MOTHER_LAST_NAME')}
                    name={`${value}.parents.mother.lastName`}
                    render={Input}
                    validate={composeValidators(requiredValidation, onlyLetters)}
                  />
                </FormRowContainer>
                <FormRowContainer>
                  <FieldRequired
                    label={i18next.t<string>('MARRIAGES_FORM:FATHER_NAME')}
                    name={`${value}.parents.father.name`}
                    render={Input}
                    validate={composeValidators(requiredValidation, onlyLetters)}
                  />
                  <FieldRequired
                    label={i18next.t<string>('MARRIAGES_FORM:MOTHER_NAME')}
                    name={`${value}.parents.mother.name`}
                    render={Input}
                    validate={composeValidators(requiredValidation, onlyLetters)}
                  />
                </FormRowContainer>
                <FormRowContainer>
                  <Field
                    label={i18next.t<string>('MARRIAGES_FORM:ID_CARD_TYPE')}
                    name={`${value}.parents.father.idCardType`}
                    options={idCardTypes}
                    render={SelectInput}
                    validate={requiredValidation}
                  />
                  <Field
                    label={i18next.t<string>('MARRIAGES_FORM:ID_CARD_TYPE')}
                    name={`${value}.parents.mother.idCardType`}
                    options={idCardTypes}
                    render={SelectInput}
                    validate={requiredValidation}
                  />
                </FormRowContainer>
                <FormRowContainer>
                  <FieldRequired
                    label={i18next.t<string>('MARRIAGES_FORM:ID_CARD')}
                    name={`${value}.parents.father.idCard`}
                    render={Input}
                    validate={requiredValidation}
                  />
                  <FieldRequired
                    label={i18next.t<string>('MARRIAGES_FORM:ID_CARD')}
                    name={`${value}.parents.mother.idCard`}
                    render={Input}
                    validate={requiredValidation}
                  />
                </FormRowContainer>
                <FormRowContainer>
                  <FieldRequired
                    label={i18next.t<string>('MARRIAGES_FORM:ID_CARD_FILE')}
                    name={`${value}.parents.father.idCardFile`}
                    previewFiles={[
                      {
                        label: i18next.t<string>('MARRIAGES_FORM:ID_CARD_FILE'),
                        value: files?.[`${value}ParentsFatherIdCardFile`],
                      },
                    ]}
                    render={Input}
                    type="file"
                    validate={
                      isEditing
                        ? fileValidation
                        : composeValidators(requiredValidation, fileValidation)
                    }
                  />
                  <FieldRequired
                    label={i18next.t<string>('MARRIAGES_FORM:ID_CARD_FILE')}
                    name={`${value}.parents.mother.idCardFile`}
                    previewFiles={[
                      {
                        label: i18next.t<string>('MARRIAGES_FORM:ID_CARD_FILE'),
                        value: files?.[`${value}ParentsMotherIdCardFile`],
                      },
                    ]}
                    render={Input}
                    type="file"
                    validate={
                      isEditing
                        ? fileValidation
                        : composeValidators(requiredValidation, fileValidation)
                    }
                  />
                </FormRowContainer>
                <FormRowContainer>
                  <FieldRequired
                    label={i18next.t<string>('MARRIAGES_FORM:ID_CARD_BACK_FILE')}
                    name={`${value}.parents.father.idCardBackFile`}
                    previewFiles={[
                      {
                        label: i18next.t<string>('MARRIAGES_FORM:ID_CARD_BACK_FILE'),
                        value: files?.[`${value}ParentsFatherIdCardBackFile`],
                      },
                    ]}
                    render={Input}
                    type="file"
                    validate={
                      isEditing
                        ? fileValidation
                        : composeValidators(requiredValidation, fileValidation)
                    }
                  />
                  <FieldRequired
                    label={i18next.t<string>('MARRIAGES_FORM:ID_CARD_BACK_FILE')}
                    name={`${value}.parents.mother.idCardBackFile`}
                    previewFiles={[
                      {
                        label: i18next.t<string>('MARRIAGES_FORM:ID_CARD_BACK_FILE'),
                        value: files?.[`${value}ParentsMotherIdCardBackFile`],
                      },
                    ]}
                    render={Input}
                    type="file"
                    validate={
                      isEditing
                        ? fileValidation
                        : composeValidators(requiredValidation, fileValidation)
                    }
                  />
                </FormRowContainer>
                <FormRowContainer>
                  <FieldRequired
                    label={i18next.t<string>('MARRIAGES_FORM:BIRTHPLACE')}
                    name={`${value}.parents.father.birthplace`}
                    render={Input}
                    validate={requiredValidation}
                  />
                  <FieldRequired
                    label={i18next.t<string>('MARRIAGES_FORM:BIRTHPLACE')}
                    name={`${value}.parents.mother.birthplace`}
                    render={Input}
                    validate={requiredValidation}
                  />
                </FormRowContainer>
                <FormRowContainer>
                  <FieldRequired
                    label={i18next.t<string>('MARRIAGES_FORM:BIRTHDATE')}
                    name={`${value}.parents.father.birthdate`}
                    render={Input}
                    type="date"
                    max={today}
                    validate={requiredValidation}
                  />
                  <FieldRequired
                    label={i18next.t<string>('MARRIAGES_FORM:BIRTHDATE')}
                    name={`${value}.parents.mother.birthdate`}
                    render={Input}
                    type="date"
                    max={today}
                    validate={requiredValidation}
                  />
                </FormRowContainer>
                <FormRowContainer>
                  <Field
                    label={i18next.t<string>('MARRIAGES_FORM:CIVIL_STATE')}
                    name={`${value}.parents.father.civilState`}
                    options={civilStates}
                    render={SelectInput}
                    validate={requiredValidation}
                  />
                  <Field
                    label={i18next.t<string>('MARRIAGES_FORM:CIVIL_STATE')}
                    name={`${value}.parents.mother.civilState`}
                    options={civilStates}
                    render={SelectInput}
                    validate={requiredValidation}
                  />
                </FormRowContainer>
                <FormRowContainer>
                  {formValues[value as 'groom' | 'bride'].parents.father.civilState ===
                    'DIVORCED' && (
                    <FieldRequired
                      label={i18next.t<string>('MARRIAGES_FORM:GET_FILE')}
                      name={`${value}.parents.father.getFile`}
                      previewFiles={[
                        {
                          label: i18next.t<string>('MARRIAGES_FORM:GET_FILE'),
                          value: files?.[`${value}ParentsFatherGetFile`],
                        },
                      ]}
                      render={Input}
                      type="file"
                      validate={
                        isEditing
                          ? fileValidation
                          : composeValidators(requiredValidation, fileValidation)
                      }
                    />
                  )}
                  {formValues[value as 'groom' | 'bride'].parents.mother.civilState ===
                    'DIVORCED' && (
                    <FieldRequired
                      label={i18next.t<string>('MARRIAGES_FORM:GET_FILE')}
                      name={`${value}.parents.mother.getFile`}
                      previewFiles={[
                        {
                          label: i18next.t<string>('MARRIAGES_FORM:GET_FILE'),
                          value: files?.[`${value}ParentsMotherGetFile`],
                        },
                      ]}
                      render={Input}
                      type="file"
                      validate={
                        isEditing
                          ? fileValidation
                          : composeValidators(requiredValidation, fileValidation)
                      }
                    />
                  )}
                </FormRowContainer>
                <FormRowContainer>
                  <Field
                    label={i18next.t<string>('MARRIAGES_FORM:TRIBE')}
                    name={`${value}.parents.father.tribe`}
                    options={tribes}
                    render={SelectInput}
                    validate={requiredValidation}
                  />
                  <Field
                    label={i18next.t<string>('MARRIAGES_FORM:TRIBE')}
                    name={`${value}.parents.mother.tribe`}
                    options={tribes}
                    render={SelectInput}
                    validate={requiredValidation}
                  />
                </FormRowContainer>
                <FormRowContainer>
                  <Field
                    label={i18next.t<string>('MARRIAGES_FORM:ALIVE')}
                    name={`${value}.parents.father.alive`}
                    render={Checkbox}
                  />
                  <Field
                    label={i18next.t<string>('MARRIAGES_FORM:ALIVE')}
                    name={`${value}.parents.mother.alive`}
                    render={Checkbox}
                  />
                </FormRowContainer>
                <FormRowContainer>
                  {!formValues[value as 'groom' | 'bride'].parents.father.alive && (
                    <Field
                      label={i18next.t<string>('MARRIAGES_FORM:CEMENTERY')}
                      name={`${value}.parents.father.cementery`}
                      options={cementeries}
                      parse={(v) => v}
                      render={SelectInput}
                    />
                  )}
                  {!formValues[value as 'groom' | 'bride'].parents.mother.alive && (
                    <Field
                      label={i18next.t<string>('MARRIAGES_FORM:CEMENTERY')}
                      name={`${value}.parents.mother.cementery`}
                      options={cementeries}
                      parse={(v) => v}
                      render={SelectInput}
                    />
                  )}
                </FormRowContainer>
                <FormRowContainer>
                  <FieldRequired
                    label={i18next.t<string>('MARRIAGES_FORM:WEDDING_TEMPLE')}
                    name={`${value}.parents.weddingTemple`}
                    render={Input}
                    validate={requiredValidation}
                  />
                  <FieldRequired
                    label={i18next.t<string>('MARRIAGES_FORM:WEDDING_DATE')}
                    name={`${value}.parents.weddingDate`}
                    render={Input}
                    type="date"
                    max={today}
                    validate={requiredValidation}
                  />
                </FormRowContainer>
                <FormRowContainer>
                  <FieldRequired
                    label={i18next.t<string>('MARRIAGES_SHOW:KETUBA_FILE')}
                    name={`${value}.parents.ketubaFile`}
                    previewFiles={[
                      {
                        label: i18next.t<string>('MARRIAGES_SHOW:KETUBA_FILE'),
                        value: files?.[`${value}ParentsKetubaFile`],
                      },
                    ]}
                    render={Input}
                    type="file"
                    validate={
                      isEditing
                        ? fileValidation
                        : composeValidators(requiredValidation, fileValidation)
                    }
                  />
                  <FieldRequired
                    label={i18next.t<string>('MARRIAGES_SHOW:MARRIAGE_CERTIFICATE_FILE')}
                    name={`${value}.parents.marriageCertificateFile`}
                    previewFiles={[
                      {
                        label: i18next.t<string>('MARRIAGES_SHOW:MARRIAGE_CERTIFICATE_FILE'),
                        value: files?.[`${value}ParentsMarriageCertificateFile`],
                      },
                    ]}
                    render={Input}
                    type="file"
                    validate={
                      isEditing
                        ? fileValidation
                        : composeValidators(requiredValidation, fileValidation)
                    }
                  />
                </FormRowContainer>
              </Fragment>
            ))}
          </MultiStepForm.Page>
          <MultiStepForm.Page key="grand-parents-page">
            {['groom', 'bride'].map((value) => (
              <Fragment key={`${value}-grandparents`}>
                <Typography variant="h6">
                  {i18next.t<string>(
                    `MARRIAGES_FORM:${value.toUpperCase()}_GRANDPARENTS_INFORMATION`,
                  )}
                </Typography>
                <br />
                <FormRowContainer>
                  <FieldRequired
                    label={i18next.t<string>('MARRIAGES_FORM:PATERNAL_FATHER_LAST_NAME')}
                    name={`${value}.grandParents.paternalFather.lastName`}
                    render={Input}
                    validate={composeValidators(requiredValidation, onlyLetters)}
                  />
                  <FieldRequired
                    label={i18next.t<string>('MARRIAGES_FORM:PATERNAL_FATHER_NAME')}
                    name={`${value}.grandParents.paternalFather.name`}
                    render={Input}
                    validate={composeValidators(requiredValidation, onlyLetters)}
                  />
                </FormRowContainer>
                <FieldRequired
                  label={i18next.t<string>('MARRIAGES_FORM:BIRTHPLACE')}
                  name={`${value}.grandParents.paternalFather.birthplace`}
                  render={Input}
                  validate={requiredValidation}
                />
                <FormRowContainer>
                  <FieldRequired
                    label={i18next.t<string>('MARRIAGES_FORM:PATERNAL_MOTHER_LAST_NAME')}
                    name={`${value}.grandParents.paternalMother.lastName`}
                    render={Input}
                    validate={composeValidators(requiredValidation, onlyLetters)}
                  />
                  <FieldRequired
                    label={i18next.t<string>('MARRIAGES_FORM:PATERNAL_MOTHER_NAME')}
                    name={`${value}.grandParents.paternalMother.name`}
                    render={Input}
                    validate={composeValidators(requiredValidation, onlyLetters)}
                  />
                </FormRowContainer>
                <FieldRequired
                  label={i18next.t<string>('MARRIAGES_FORM:BIRTHPLACE')}
                  name={`${value}.grandParents.paternalMother.birthplace`}
                  render={Input}
                  validate={requiredValidation}
                />
                <FormRowContainer>
                  <FieldRequired
                    label={i18next.t<string>('MARRIAGES_SHOW:KETUBA_FILE')}
                    name={`${value}.grandParents.paternalKetubaFile`}
                    previewFiles={[
                      {
                        label: i18next.t<string>('MARRIAGES_SHOW:KETUBA_FILE'),
                        value: files?.[`${value}GrandParentsPaternalKetubaFile`],
                      },
                    ]}
                    render={Input}
                    type="file"
                    validate={fileValidation}
                  />
                  <FieldRequired
                    label={i18next.t<string>('MARRIAGES_SHOW:MARRIAGE_CERTIFICATE_FILE')}
                    name={`${value}.grandParents.paternalMarriageCertificateFile`}
                    previewFiles={[
                      {
                        label: i18next.t<string>('MARRIAGES_SHOW:MARRIAGE_CERTIFICATE_FILE'),
                        value: files?.[`${value}GrandParentsPaternalMarriageCertificateFile`],
                      },
                    ]}
                    render={Input}
                    type="file"
                    validate={fileValidation}
                  />
                </FormRowContainer>
                <FormRowContainer>
                  <FieldRequired
                    label={i18next.t<string>('MARRIAGES_FORM:MATERNAL_FATHER_LAST_NAME')}
                    name={`${value}.grandParents.maternalFather.lastName`}
                    render={Input}
                    validate={composeValidators(requiredValidation, onlyLetters)}
                  />
                  <FieldRequired
                    label={i18next.t<string>('MARRIAGES_FORM:MATERNAL_FATHER_NAME')}
                    name={`${value}.grandParents.maternalFather.name`}
                    render={Input}
                    validate={composeValidators(requiredValidation, onlyLetters)}
                  />
                </FormRowContainer>
                <FieldRequired
                  label={i18next.t<string>('MARRIAGES_FORM:BIRTHPLACE')}
                  name={`${value}.grandParents.maternalFather.birthplace`}
                  render={Input}
                  validate={requiredValidation}
                />
                <FormRowContainer>
                  <FieldRequired
                    label={i18next.t<string>('MARRIAGES_FORM:MATERNAL_MOTHER_LAST_NAME')}
                    name={`${value}.grandParents.maternalMother.lastName`}
                    render={Input}
                    validate={composeValidators(requiredValidation, onlyLetters)}
                  />
                  <FieldRequired
                    label={i18next.t<string>('MARRIAGES_FORM:MATERNAL_MOTHER_NAME')}
                    name={`${value}.grandParents.maternalMother.name`}
                    render={Input}
                    validate={composeValidators(requiredValidation, onlyLetters)}
                  />
                </FormRowContainer>
                <FieldRequired
                  label={i18next.t<string>('MARRIAGES_FORM:BIRTHPLACE')}
                  name={`${value}.grandParents.maternalMother.birthplace`}
                  render={Input}
                  validate={requiredValidation}
                />
                <FormRowContainer>
                  <FieldRequired
                    label={i18next.t<string>('MARRIAGES_SHOW:KETUBA_FILE')}
                    name={`${value}.grandParents.maternalKetubaFile`}
                    previewFiles={[
                      {
                        label: i18next.t<string>('MARRIAGES_SHOW:KETUBA_FILE'),
                        value: files?.[`${value}GrandParentsMaternalKetubaFile`],
                      },
                    ]}
                    render={Input}
                    type="file"
                    validate={fileValidation}
                  />
                  <FieldRequired
                    label={i18next.t<string>('MARRIAGES_SHOW:MARRIAGE_CERTIFICATE_FILE')}
                    name={`${value}.grandParents.maternalMarriageCertificateFile`}
                    previewFiles={[
                      {
                        label: i18next.t<string>('MARRIAGES_SHOW:MARRIAGE_CERTIFICATE_FILE'),
                        value: files?.[`${value}GrandParentsMaternalMarriageCertificateFile`],
                      },
                    ]}
                    render={Input}
                    type="file"
                    validate={fileValidation}
                  />
                </FormRowContainer>
              </Fragment>
            ))}
          </MultiStepForm.Page>
          <MultiStepForm.Page key="witnesses-page">
            {['groom', 'bride'].map((value) => (
              <Fragment key={`${value}-witnesses`}>
                <Typography variant="h6">
                  {i18next.t<string>(`MARRIAGES_FORM:${value.toUpperCase()}_WITNESSES_INFORMATION`)}
                </Typography>
                <br />
                {(formValues[value as 'groom' | 'bride']?.witnesses as Array<unknown>)?.map(
                  (_witness: unknown, i: number) => (
                    <Fragment key={`witness-${i.toString()}`}>
                      <FormRowContainer>
                        <FieldRequired
                          label={`${i18next.t<string>('MARRIAGES_FORM:WITNESS_NAME')} ${i + 1}`}
                          name={`${value}.witnesses[${i}].name`}
                          parse={(v: string) => v}
                          render={Input}
                          validate={composeValidators(requiredValidation, onlyLetters)}
                        />
                        <FieldRequired
                          label={i18next.t<string>('MARRIAGES_FORM:EMAIL')}
                          name={`${value}.witnesses[${i}].email`}
                          parse={(v: string) => v}
                          render={Input}
                          type="email"
                          validate={emailValidation}
                        />
                      </FormRowContainer>
                      <FormRowContainer>
                        <Field
                          label={i18next.t<string>('MARRIAGES_FORM:ID_CARD_TYPE')}
                          name={`${value}.witnesses[${i}].idCardType`}
                          options={idCardTypes}
                          parse={(v) => v}
                          render={SelectInput}
                        />
                        <Field
                          label={i18next.t<string>('MARRIAGES_FORM:ID_CARD')}
                          name={`${value}.witnesses[${i}].idCard`}
                          parse={(v) => v}
                          render={Input}
                        />
                      </FormRowContainer>
                      <FormRowContainer>
                        <FieldRequired
                          label={i18next.t<string>('MARRIAGES_FORM:ID_CARD_FILE')}
                          name={`${value}.witnesses[${i}].idCardFile`}
                          previewFiles={[
                            {
                              label: i18next.t<string>('MARRIAGES_FORM:ID_CARD_FILE'),
                              value: files?.[`${value}WitnessesIdCardFiles`]?.[i],
                            },
                          ]}
                          render={Input}
                          type="file"
                          validate={fileValidation}
                        />
                        <FieldRequired
                          label={i18next.t<string>('MARRIAGES_FORM:ID_CARD_BACK_FILE')}
                          name={`${value}.witnesses[${i}].idCardBackFile`}
                          previewFiles={[
                            {
                              label: i18next.t<string>('MARRIAGES_FORM:ID_CARD_BACK_FILE'),
                              value: files?.[`${value}WitnessesIdCardBackFiles`]?.[i],
                            },
                          ]}
                          render={Input}
                          type="file"
                          validate={fileValidation}
                        />
                      </FormRowContainer>
                      <FormRowContainer>
                        <Field
                          label={i18next.t<string>('MARRIAGES_FORM:ADDRESS')}
                          name={`${value}.witnesses[${i}].address`}
                          parse={(v) => v}
                          render={Input}
                        />
                        <FormRowContainer>
                          <Field
                            label={i18next.t<string>('MARRIAGES_FORM:AREA_CODE')}
                            maxLength={4}
                            name={`${value}.witnesses[${i}].areaCode`}
                            parse={(v) => digitsOnly(v)}
                            render={Input}
                          />
                          <Field
                            label={i18next.t<string>('MARRIAGES_FORM:PHONE')}
                            maxLength={8}
                            name={`${value}.witnesses[${i}].phone`}
                            parse={(v) => digitsOnly(v)}
                            render={Input}
                          />
                        </FormRowContainer>
                      </FormRowContainer>
                      <FormRowContainer>
                        <FieldRequired
                          label={i18next.t<string>('MARRIAGES_FORM:KETUBA_FILE')}
                          name={`${value}.witnesses[${i}].ketubaFile`}
                          previewFiles={[
                            {
                              label: i18next.t<string>('MARRIAGES_FORM:KETUBA_FILE'),
                              value: files?.[`${value}WitnessesKetubaFiles`]?.[i],
                            },
                          ]}
                          render={Input}
                          type="file"
                          validate={fileValidation}
                        />
                        <FieldRequired
                          label={i18next.t<string>('MARRIAGES_FORM:MARRIAGE_CERTIFICATE_FILE')}
                          name={`${value}.witnesses[${i}].marriageCertificateFile`}
                          previewFiles={[
                            {
                              label: i18next.t<string>('MARRIAGES_FORM:MARRIAGE_CERTIFICATE_FILE'),
                              value: files?.[`${value}WitnessesMarriageCertificateFiles`]?.[i],
                            },
                          ]}
                          render={Input}
                          type="file"
                          validate={fileValidation}
                        />
                      </FormRowContainer>
                    </Fragment>
                  ),
                )}
              </Fragment>
            ))}
          </MultiStepForm.Page>
          <MultiStepForm.Page key="ceremony-page">
            <Typography variant="h6">
              {i18next.t<string>('MARRIAGES_FORM:CEREMONY_INFORMATION')}
            </Typography>
            <br />
            <FormRowContainer>
              <FieldRequired
                label={i18next.t<string>('MARRIAGES_FORM:ADDRESS')}
                name="ceremony.address"
                render={Input}
                validate={requiredValidation}
              />
              <FieldRequired
                label={i18next.t<string>('MARRIAGES_FORM:LOCALITY')}
                name="ceremony.locality"
                render={Input}
                validate={requiredValidation}
              />
            </FormRowContainer>
            <FormRowContainer>
              <FieldRequired
                label={i18next.t<string>('MARRIAGES_FORM:CEREMONY_RELIGIOUS_DATE')}
                name="ceremony.religiousDate"
                render={Input}
                type="datetime-local"
                validate={requiredValidation}
              />
              <Field
                label={i18next.t<string>('MARRIAGES_FORM:CEREMONY_CIVIL_DATE')}
                name="ceremony.civilDate"
                parse={(v) => v}
                render={Input}
                type="datetime-local"
              />
            </FormRowContainer>
            <FormRowContainer>
              <FieldRequired
                label={i18next.t<string>('MARRIAGES_FORM:CEREMONY_OFFICIANT')}
                name="ceremony.officiant"
                render={Input}
                validate={requiredValidation}
              />
              <FieldRequired
                label={i18next.t<string>('MARRIAGES_FORM:CEREMONY_CIVIL_LOCALITY')}
                name="ceremony.civilLocality"
                render={Input}
                validate={requiredValidation}
              />
            </FormRowContainer>
          </MultiStepForm.Page>
          <MultiStepForm.Page key="comments-page">
            <Field
              label={i18next.t<string>('MARRIAGES_FORM:COMMENTS')}
              name="comments"
              parse={(value) => value}
              render={Input}
              type="textarea"
            />
          </MultiStepForm.Page>
        </MultiStepForm>
      </ViewContent>
    </ViewContainer>
  );
};

export default MarriagesForm;
